/**
 * 航线草稿：用户还没上传的航线不能丢。
 *
 * ## 为什么草稿必须自动保存，以及"节流"而不是"防抖"
 *
 * 画一条 30 个航点的测绘航线要几分钟。浏览器崩溃、误关标签页、切到别的应用被系统回收 ——
 * 这些都不给用户"再点一次保存"的机会。所以草稿必须自动落盘。
 *
 * 但**不能每次改动都写**：拖动航点时每帧都会产生一次改动，逐次写会在一秒钟里发上百个事务，
 * 既拖慢地图又白白消耗配额。这里采用**节流（throttle）而不是防抖（debounce）**：
 *
 * - 防抖（每次改动重置计时器）在"用户持续编辑两分钟"时一次都不会保存 —— 而这恰恰是最需要
 *   保存的两分钟，一崩全丢。
 * - 节流从上一次写入算起 `intervalMs`（默认 2000ms）写一次，**丢失窗口有明确上界**。
 *
 * ## 原子性
 *
 * 草稿正文与"上次编辑的是哪一份"指针在同一个 `batch()` 里写。分开写的话，写到一半崩溃就会
 * 留下"指针指向一份不存在的草稿"，崩溃恢复直接落空 —— 而崩溃恢复正是这个功能存在的唯一理由。
 *
 * ## 并发写：最后写入胜出
 *
 * 两个标签页编辑同一份草稿时，最后写入的文件胜出，**不做行级合并**。图元参数合并没有
 * 正确的通用解（两个标签页各自移动了同一个航点，合并出的位置谁都没想要）。
 * 但覆盖会通过 `conflict` 明确回报，界面据此提示"你在另一个标签页的改动已被覆盖"。
 */

import type { MissionHomePosition, MissionItem, MissionPlan } from '../mission/types';
import { isMissionTypeId } from '../mission/types';

import { describeStorageFailure, runSave, saveOk, type SaveOutcome, type StorageFailure } from './errors';
import {
  MISSION_DRAFT_LAST_KEY,
  MISSION_DRAFT_PREFIX,
  RECORD_FORMAT,
  isReadableFormat,
  isRecordLike,
  missionDraftKey,
  readFiniteNumber,
  readString,
} from './keys';
import { estimateStorableBytes } from './serialization';
import type { KeyValueStore, StoreOperation } from './store';

export interface MissionDraftRecord {
  readonly format: number;
  readonly draftId: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly plan: MissionPlan;
  /** 草稿的估算占用，用于草稿列表显示与"清空草稿"前的提示 */
  readonly bytes: number;
  readonly name?: string;
  readonly vehicleSystemId?: number;
  readonly notes?: string;
  readonly home?: MissionHomePosition;
  /** 地图中心/缩放等编辑态，让"恢复上次编辑"能回到同一个视角 */
  readonly editorState?: Readonly<Record<string, unknown>>;
}

export interface MissionDraftInit {
  readonly plan: MissionPlan;
  readonly draftId?: string;
  readonly name?: string;
  readonly vehicleSystemId?: number;
  readonly notes?: string;
  readonly home?: MissionHomePosition;
  readonly editorState?: Readonly<Record<string, unknown>>;
  readonly updatedAt?: number;
}

function readMissionItem(value: unknown): MissionItem | null {
  if (!isRecordLike(value)) return null;
  const seq = readFiniteNumber(value['seq']);
  const command = readFiniteNumber(value['command']);
  const frame = readFiniteNumber(value['frame']);
  const current = value['current'];
  const autocontinue = value['autocontinue'];
  const param1 = readFiniteNumber(value['param1']);
  const param2 = readFiniteNumber(value['param2']);
  const param3 = readFiniteNumber(value['param3']);
  const param4 = readFiniteNumber(value['param4']);
  const latDeg = readFiniteNumber(value['latDeg']);
  const lonDeg = readFiniteNumber(value['lonDeg']);
  const altM = readFiniteNumber(value['altM']);
  if (seq === null || command === null || frame === null) return null;
  if (typeof current !== 'boolean' || typeof autocontinue !== 'boolean') return null;
  if (
    param1 === null ||
    param2 === null ||
    param3 === null ||
    param4 === null ||
    latDeg === null ||
    lonDeg === null ||
    altM === null
  ) {
    return null;
  }
  return { seq, command, frame, current, autocontinue, param1, param2, param3, param4, latDeg, lonDeg, altM };
}

export function readMissionPlan(value: unknown): MissionPlan | null {
  if (!isRecordLike(value)) return null;
  const missionType = readFiniteNumber(value['missionType']);
  if (missionType === null || !isMissionTypeId(missionType)) return null;
  const rawItems = value['items'];
  if (!Array.isArray(rawItems)) return null;
  const items: MissionItem[] = [];
  for (const entry of rawItems) {
    const item = readMissionItem(entry);
    if (item === null) return null;
    items.push(item);
  }
  return { missionType, items };
}

function readHome(value: unknown): MissionHomePosition | null {
  if (!isRecordLike(value)) return null;
  const latDeg = readFiniteNumber(value['latDeg']);
  const lonDeg = readFiniteNumber(value['lonDeg']);
  const altM = readFiniteNumber(value['altM']);
  if (latDeg === null || lonDeg === null || altM === null) return null;
  return { latDeg, lonDeg, altM };
}

/** 读取校验：不认识的草稿返回 null（调用方当作"这份草稿不可用"，而不是拿半份数据去上传） */
export function readMissionDraftRecord(value: unknown): MissionDraftRecord | null {
  if (!isRecordLike(value)) return null;
  if (!isReadableFormat(value['format'])) return null;
  const draftId = readString(value['draftId']);
  const createdAt = readFiniteNumber(value['createdAt']);
  const updatedAt = readFiniteNumber(value['updatedAt']);
  const plan = readMissionPlan(value['plan']);
  if (draftId === null || createdAt === null || updatedAt === null || plan === null) return null;
  const name = readString(value['name']);
  const notes = readString(value['notes']);
  const vehicleSystemId = readFiniteNumber(value['vehicleSystemId']);
  const home = readHome(value['home']);
  const editorState = isRecordLike(value['editorState']) ? value['editorState'] : null;
  return {
    format: RECORD_FORMAT,
    draftId,
    createdAt,
    updatedAt,
    plan,
    bytes: readFiniteNumber(value['bytes']) ?? 0,
    ...(name === null ? {} : { name }),
    ...(notes === null ? {} : { notes }),
    ...(vehicleSystemId === null ? {} : { vehicleSystemId }),
    ...(home === null ? {} : { home }),
    ...(editorState === null ? {} : { editorState }),
  };
}

export interface MissionDraftLastIndex {
  readonly format: number;
  readonly draftId: string;
  readonly updatedAt: number;
}

export function readDraftIndex(value: unknown): MissionDraftLastIndex | null {
  if (!isRecordLike(value)) return null;
  const draftId = readString(value['draftId']);
  const updatedAt = readFiniteNumber(value['updatedAt']);
  if (draftId === null || updatedAt === null) return null;
  return { format: readFiniteNumber(value['format']) ?? RECORD_FORMAT, draftId, updatedAt };
}

export interface MissionDraftRepositoryOptions {
  readonly now?: () => number;
  readonly onFailure?: (failure: StorageFailure) => void;
  /** 本地保留的草稿份数上限，默认 5。超过时删掉最旧的那份 */
  readonly maxDrafts?: number;
  /** 草稿 id 生成器，测试里注入确定性实现 */
  readonly newId?: () => string;
}

export interface SaveMissionDraftOptions {
  /**
   * 调用方上次看到的 `updatedAt`。与库里不一致时仍然写入（最后写入胜出），但会回报 `conflict`。
   */
  readonly expectedUpdatedAt?: number;
}

export class MissionDraftRepository {
  private readonly store: KeyValueStore;
  private readonly now: () => number;
  private readonly onFailure: ((failure: StorageFailure) => void) | undefined;
  private readonly maxDrafts: number;
  private readonly newId: () => string;

  constructor(store: KeyValueStore, options: MissionDraftRepositoryOptions = {}) {
    this.store = store;
    this.now = options.now ?? Date.now;
    this.maxDrafts = options.maxDrafts ?? 5;
    this.newId = options.newId ?? defaultDraftId;
  }

  async save(
    init: MissionDraftInit,
    options: SaveMissionDraftOptions = {},
  ): Promise<SaveOutcome<MissionDraftRecord>> {
    const outcome = await runSave(
      '保存航线草稿',
      async () => {
        const draftId = init.draftId ?? this.newId();
        const updatedAt = init.updatedAt ?? this.now();
        const existingRaw = await this.store.get('mission-drafts', missionDraftKey(draftId));
        const existing = readMissionDraftRecord(existingRaw);
        const createdAt = existing?.createdAt ?? updatedAt;

        const record: MissionDraftRecord = {
          format: RECORD_FORMAT,
          draftId,
          createdAt,
          updatedAt,
          plan: init.plan,
          bytes: 0,
          ...(init.name === undefined ? {} : { name: init.name }),
          ...(init.vehicleSystemId === undefined ? {} : { vehicleSystemId: init.vehicleSystemId }),
          ...(init.notes === undefined ? {} : { notes: init.notes }),
          ...(init.home === undefined ? {} : { home: init.home }),
          ...(init.editorState === undefined ? {} : { editorState: init.editorState }),
        };
        const stored: MissionDraftRecord = { ...record, bytes: estimateStorableBytes(record) };

        const operations: StoreOperation[] = [
          { kind: 'put', collection: 'mission-drafts', key: missionDraftKey(draftId), value: stored },
        ];

        // 指针只在"这份确实是最新的"时才更新：否则从旧标签页保存一份旧草稿会把
        // "上次编辑的内容"指回过去，用户下次打开看到的不是最近编辑的那份
        const pointer = readDraftIndex(await this.store.get('mission-drafts', MISSION_DRAFT_LAST_KEY));
        if (pointer === null || stored.updatedAt >= pointer.updatedAt) {
          operations.push({
            kind: 'put',
            collection: 'mission-drafts',
            key: MISSION_DRAFT_LAST_KEY,
            value: { format: RECORD_FORMAT, draftId, updatedAt: stored.updatedAt } satisfies MissionDraftLastIndex,
          });
        }

        // 份数上限：与写入放在同一个 batch，避免"写成功了但裁剪失败"导致草稿无限增长
        const all = await this.list();
        const victims = all.filter((draft) => draft.draftId !== draftId).slice(this.maxDrafts - 1);
        for (const victim of victims) {
          operations.push({
            kind: 'delete',
            collection: 'mission-drafts',
            key: missionDraftKey(victim.draftId),
          });
        }

        await this.store.batch(operations);

        const expected = options.expectedUpdatedAt;
        if (expected === undefined || existing === null || existing.updatedAt === expected) {
          return saveOk(stored);
        }
        return saveOk(stored, {
          expectedUpdatedAt: expected,
          actualUpdatedAt: existing.updatedAt,
          message: `这份草稿已被其它标签页修改（你读到的时间戳是 ${expected}，库里已是 ${existing.updatedAt}），本次保存按「最后写入胜出」覆盖了对方的改动。`,
        });
      },
      { collection: 'mission-drafts' },
    );

    if (!outcome.ok) this.onFailure?.(outcome.failure);
    return outcome;
  }

  async load(draftId: string): Promise<MissionDraftRecord | null> {
    return readMissionDraftRecord(await this.store.get('mission-drafts', missionDraftKey(draftId)));
  }

  /** "上次编辑的草稿"。崩溃或误关页面后，界面靠它恢复现场 */
  async latest(): Promise<MissionDraftRecord | null> {
    const pointer = readDraftIndex(await this.store.get('mission-drafts', MISSION_DRAFT_LAST_KEY));
    if (pointer !== null) {
      const pointed = await this.load(pointer.draftId);
      if (pointed !== null) return pointed;
      // 指针指向的草稿不在了（被清理或写入失败）：继续往下按 updatedAt 找，而不是直接返回 null
    }
    const all = await this.list();
    return all[0] ?? null;
  }

  /** 按 `updatedAt` 倒序列出草稿 */
  async list(): Promise<readonly MissionDraftRecord[]> {
    const keys = await this.store.keys('mission-drafts', MISSION_DRAFT_PREFIX);
    const drafts: MissionDraftRecord[] = [];
    for (const key of keys) {
      const draft = readMissionDraftRecord(await this.store.get('mission-drafts', key));
      if (draft !== null) drafts.push(draft);
    }
    drafts.sort((left, right) => right.updatedAt - left.updatedAt);
    return drafts;
  }

  async listRecent(limit = this.maxDrafts): Promise<readonly MissionDraftRecord[]> {
    return (await this.list()).slice(0, limit);
  }

  async remove(draftId: string): Promise<SaveOutcome<boolean>> {
    const outcome = await runSave(
      '删除航线草稿',
      async () => {
        const key = missionDraftKey(draftId);
        const existing = await this.store.get('mission-drafts', key);
        if (existing === undefined) return saveOk(false);
        const operations: StoreOperation[] = [{ kind: 'delete', collection: 'mission-drafts', key }];
        // 删掉的正好是指针指向的那份时，把指针改到次新的草稿上；
        // 留着指向已删除草稿的指针，下次打开会走一遍"指针失效"的兜底路径
        const pointer = readDraftIndex(await this.store.get('mission-drafts', MISSION_DRAFT_LAST_KEY));
        if (pointer !== null && pointer.draftId === draftId) {
          const next = (await this.list()).find((draft) => draft.draftId !== draftId);
          if (next === undefined) {
            operations.push({ kind: 'delete', collection: 'mission-drafts', key: MISSION_DRAFT_LAST_KEY });
          } else {
            operations.push({
              kind: 'put',
              collection: 'mission-drafts',
              key: MISSION_DRAFT_LAST_KEY,
              value: { format: RECORD_FORMAT, draftId: next.draftId, updatedAt: next.updatedAt },
            });
          }
        }
        await this.store.batch(operations);
        return saveOk(true);
      },
      { collection: 'mission-drafts' },
    );
    if (!outcome.ok) this.onFailure?.(outcome.failure);
    return outcome;
  }
}

function defaultDraftId(): string {
  // 时间戳 + 随机后缀：两个标签页在同一毫秒各建一份草稿也不会撞 id
  return `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export interface MissionDraftAutosaveOptions {
  /** 两次自动保存之间的最小间隔，默认 2000ms */
  readonly intervalMs?: number;
  readonly now?: () => number;
  /** 大于 0 时用真实定时器自动推进；默认 0，由调用方（或测试）显式 `tick()` */
  readonly autoTickMs?: number;
  readonly onSaved?: (draft: MissionDraftRecord) => void;
  readonly onFailed?: (failure: StorageFailure) => void;
}

/**
 * 草稿自动保存器。
 *
 * 与 `MissionManager` 的 `tick()` 约定一致：时间由注入的时钟给出，`tick()` 由调用方显式推进，
 * 因此"过多久才写一次"这件事在测试里是确定的，不需要 sleep。
 */
export class MissionDraftAutosave {
  private readonly repository: MissionDraftRepository;
  private readonly intervalMs: number;
  private readonly now: () => number;
  private readonly onSaved: ((draft: MissionDraftRecord) => void) | undefined;
  private readonly onFailed: ((failure: StorageFailure) => void) | undefined;

  private staged: MissionDraftInit | null = null;
  private dirtySince: number | null = null;
  private inFlight: Promise<SaveOutcome<MissionDraftRecord>> | null = null;
  private lastSavedAt: number | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(repository: MissionDraftRepository, options: MissionDraftAutosaveOptions = {}) {
    this.repository = repository;
    this.intervalMs = options.intervalMs ?? 2000;
    this.now = options.now ?? Date.now;
    this.onSaved = options.onSaved;
    this.onFailed = options.onFailed;
    if ((options.autoTickMs ?? 0) > 0) {
      this.timer = setInterval(() => {
        this.tick();
      }, options.autoTickMs);
    }
  }

  /** 有未落盘的改动 */
  get pending(): boolean {
    return this.staged !== null || this.inFlight !== null;
  }

  get savedAt(): number | null {
    return this.lastSavedAt;
  }

  /** 记录一次改动。**不**立即写盘，等 `tick()` 到点或 `flush()` */
  update(init: MissionDraftInit): void {
    this.staged = init;
    this.dirtySince ??= this.now();
  }

  /**
   * 推进时间。到点（距第一次改动 ≥ `intervalMs`）就发起一次保存。
   * 返回是否发起了写入，方便界面显示"已自动保存"。
   */
  tick(): boolean {
    if (this.staged === null || this.dirtySince === null) return false;
    if (this.now() - this.dirtySince < this.intervalMs) return false;
    this.write();
    return true;
  }

  /** 立刻落盘（切页、关闭页面前调用）。没有待保存内容时返回 null */
  async flush(): Promise<SaveOutcome<MissionDraftRecord> | null> {
    if (this.inFlight !== null) await this.inFlight;
    if (this.staged === null) return null;
    return await this.write();
  }

  /** 停止自动 tick（组件卸载时调用） */
  stop(): void {
    if (this.timer === null) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  private write(): Promise<SaveOutcome<MissionDraftRecord>> {
    const staged = this.staged;
    if (staged === null) throw new Error('没有待保存的草稿');
    this.staged = null;
    this.dirtySince = null;
    const pending = this.repository.save(staged);
    this.inFlight = pending;
    void pending
      .then((outcome) => {
        if (this.inFlight === pending) this.inFlight = null;
        if (outcome.ok) {
          this.lastSavedAt = outcome.value.updatedAt;
          this.onSaved?.(outcome.value);
          return;
        }
        // 保存失败（多半是配额满）时把内容放回待保存状态：下一次 tick 会再试一次。
        // 直接丢掉的话，用户"一直在编辑、以为存住了"的这段时间里，改动只存在于内存里
        if (this.staged === null) {
          this.staged = staged;
          this.dirtySince = this.now();
        }
        this.onFailed?.(outcome.failure);
      })
      .catch((error: unknown) => {
        // 仓库层不抛错（失败一律走返回值），这里是最后一道保险：
        // 自动保存的失败消息绝不能变成一条未处理的 Promise 拒绝
        this.onFailed?.(describeStorageFailure(error, { operation: '自动保存航线草稿' }));
      });
    return pending;
  }
}
